import { INSTALLMENT_BILLING, RECURRING_BILLING, WORKING_DAYS_PER_MONTH } from '../../constants';
import type { Deal, DealLine } from '../../types/domain';
import { contractMonthsInYear, contractTotalMonths } from '../dates';
import { toNumber } from '../numbers';
import { soldInitialMonths, soldOpenMonths } from './contractLength';
import { hourlySoldMonthly } from './hourly';
import { activeLines } from './lines';
import { regieValue } from './regie';
import type { Year } from './types';

// Montant mensuel "vendu" (réel, signé) d'une ligne récurrente. Maintenance/hébergement : montant_mensuel
// saisi directement. Régie : dérivé du taux vendu (nb_devs_vendu × tjm_vendu × jours ouvrés/mois) —
// pas de champ mensuel direct, même formule que l'auto-calcul de budget_vendu au formulaire.
export function _soldMonthlyAmount(l: DealLine): number {
  const fact = l.facturation || 'forfait_brique';
  // Forfait échelonné : montant total ÷ nombre de mensualités (dates, sinon engagement).
  if (fact === INSTALLMENT_BILLING) {
    const months =
      contractTotalMonths(l.date_debut_prevue, l.date_fin_prevue) || soldInitialMonths(l);
    return (toNumber(l.budget_vendu) || 0) / months;
  }
  if (fact === 'regie_horaire') return hourlySoldMonthly(l);
  if (fact === 'regie_temporelle') {
    const ndv = toNumber(l.nb_devs_vendu || 0),
      tjmv = toNumber(l.tjm_vendu || 0);
    return ndv * tjmv * WORKING_DAYS_PER_MONTH;
  }
  return toNumber(l.montant_mensuel || 0) || 0;
}

// Année de décision d'une affaire (passage à "Devis accepté") — sert à poser tout le CA one-shot
// (forfait) sur une seule année plutôt que de le répartir, même si le projet livré s'étale ensuite
// sur plusieurs mois/années (règle métier du 2026-09-02 : "on compte le CA quand on commence à
// facturer, sur l'année de conversion, même si ça fausse un peu").
// Pas de repli automatique si date_devis_accepte est absente : mieux vaut que le CA one-shot de
// cette ligne n'apparaisse dans AUCUNE année précise (mais reste dans le total "all") plutôt que de
// deviner une année et fausser un chiffre déjà signé — demander la vraie date plutôt que deviner.
export function dealDecisionYear(a: Deal): string | null {
  return a.date_devis_accepte ? a.date_devis_accepte.slice(0, 4) : null;
}

// Chiffre d'affaires d'une ligne vendue pour une année donnée.
// Récurrent (maintenance/hébergement/régie) : montant mensuel × mois du contrat dans l'année.
// One-shot (forfait) : montant vendu en entier sur l'année de décision (decisionYear), 0 sinon.
export function _lineRevenueForYear(l: DealLine, year: Year, decisionYear: string | null): number {
  const fact = l.facturation || 'forfait_brique';
  if (fact === 'regie_temporelle' && l.date_debut_prevue)
    return regieValue(l, l.nb_devs_vendu, l.tjm_vendu, soldOpenMonths(l), year);
  if (RECURRING_BILLING.includes(fact as string)) {
    const mois = contractMonthsInYear(
      l.date_debut_prevue,
      l.date_fin_prevue,
      year,
      soldOpenMonths(l),
    );
    return _soldMonthlyAmount(l) * mois;
  }
  if (!decisionYear || String(year) !== decisionYear) return 0;
  return toNumber(l.budget_vendu || 0) || 0;
}

// CA total d'une ligne vendue (toutes années). Récurrent → sur toute la durée du contrat.
// Contrat ouvert (pas de date de fin) → valeur annuelle : 12 mois pleins tant que non résilié.
// Récurrent sans date de début → 0 : la somme des années doit toujours égaler le total (la date
// de début est exigée pour passer en « Devis accepté »).
export function _lineRevenueTotal(l: DealLine): number {
  const fact = l.facturation || 'forfait_brique';
  if (fact === 'regie_temporelle' && l.date_debut_prevue)
    return regieValue(l, l.nb_devs_vendu, l.tjm_vendu, soldOpenMonths(l));
  if (RECURRING_BILLING.includes(fact as string)) {
    const mensuel = _soldMonthlyAmount(l);
    let mois: number;
    if (l.date_debut_prevue && !l.date_fin_prevue)
      mois = soldOpenMonths(l); // sans fin → engagement, sinon 1 an
    else mois = contractTotalMonths(l.date_debut_prevue, l.date_fin_prevue);
    return mensuel * mois;
  }
  return toNumber(l.budget_vendu || 0) || 0;
}

// CA d'une affaire pour une année donnée, toutes lignes vendues confondues.
export function dealRevenueForYear(a: Deal, year: Year): number {
  const decisionYear = dealDecisionYear(a);
  return activeLines(a).reduce((s, l) => s + _lineRevenueForYear(l, year, decisionYear), 0);
}

// CA total d'une affaire (toutes années), toutes lignes vendues confondues.
export function dealRevenueTotal(a: Deal): number {
  return activeLines(a).reduce((s, l) => s + _lineRevenueTotal(l), 0);
}

// Valeur "vendue" pour une affaire : lignes récurrentes → CA étalé ; lignes one-shot → budget_vendu
// en entier sur l'année de décision (jamais réparti, cf. affaireDecisionYear).
export function dealSoldRevenue(a: Deal, year: Year): number {
  const decisionYear = dealDecisionYear(a);
  return activeLines(a).reduce(
    (s, l) =>
      s +
      (RECURRING_BILLING.includes(l.facturation as string)
        ? year === 'all'
          ? _lineRevenueTotal(l)
          : _lineRevenueForYear(l, year, decisionYear)
        : year === 'all' || String(year) === decisionYear
          ? toNumber(l.budget_vendu) || 0
          : 0),
    0,
  );
}
